/**
 * Configure AuthManager to use NW.js file storage before auth singleton loads.
 * Import this module FIRST from device-main.ts — never import @api-client/auth/auth here.
 */
import { configureAuthStorage, type AuthStorage } from '@api-client/auth/auth.storage';
import { createIdbAuthStorage } from '@api-client/auth/auth.idb';

const AUTH_FILE_NAME = 'zmg_auth_state.json';

function getAuthFilePath(): string {
    const path = require('path');
    return path.join(nw.App.dataPath, AUTH_FILE_NAME);
}

function createNwFileAuthStorage<T extends { accessToken: string | null }>(
    emptyState: T
): AuthStorage<T> {
    const idbStorage = createIdbAuthStorage(emptyState);

    const loadFromFile = (): T | null => {
        try {
            const fs = require('fs');
            const filePath = getAuthFilePath();
            if (!fs.existsSync(filePath)) return null;
            const raw = fs.readFileSync(filePath, 'utf8');
            if (!raw) return null;
            return JSON.parse(raw) as T;
        } catch (error) {
            console.warn('[auth] NW file load failed:', error);
            return null;
        }
    };

    const saveToFile = (state: T | null): void => {
        try {
            const fs = require('fs');
            const filePath = getAuthFilePath();
            if (state?.accessToken) {
                // Write temp then rename — atomic so a crash never leaves a corrupt auth file
                const tmpPath = filePath + '.tmp';
                fs.writeFileSync(tmpPath, JSON.stringify(state), 'utf8');
                fs.renameSync(tmpPath, filePath);
            } else if (fs.existsSync(filePath)) {
                fs.unlinkSync(filePath);
            }
        } catch (error) {
            console.warn('[auth] NW file save failed:', error);
        }
    };

    return {
        load: async () => {
            const fromFile = loadFromFile();
            if (fromFile?.accessToken) {
                return fromFile;
            }

            // One-time migrate from IndexedDB so already-paired devices stay logged in
            const fromIdb = await idbStorage.load();
            if (fromIdb?.accessToken) {
                saveToFile(fromIdb);
                return fromIdb;
            }

            return emptyState;
        },

        save: async (state) => {
            saveToFile(state.accessToken ? state : null);
        },

        clear: async () => {
            saveToFile(null);
        },
    };
}

if (typeof nw !== 'undefined') {
    configureAuthStorage(createNwFileAuthStorage);
}
